import type { FC } from "react";
import {
  GetManyOrdersOfUserQuery,
  Order,
  OrderStatus as GqlOrderStatus,
  UpdateOrderDocument,
} from "/@/generated/graphql";
import { Box, Button, Flex, Group, Text, Title } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { OrderStatusStepper } from "/@/pages/orders/OrderStatusStepper";
import { getProductsOrderedCountLabel } from "/@/shared/utils/products/products.utils";
import { openConfirmModal } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@apollo/client";
import { useAuthContext } from "/@/shared/contexts/AuthContext";

import {
  getOrderStatusStep,
  getPatientLabelFirstLine,
  OrderStatus as SharedOrderStatus,
} from "@jasper/shared";
import { useResumeDraftOrder } from "./useResumeDraftOrder";

type OrderSummaryRowProps = {
  order: GetManyOrdersOfUserQuery["getManyOrdersOfUser"][0];
  variant?: "default" | "compact";
  isLast?: boolean;
};

export const OrderSummaryRow: FC<OrderSummaryRowProps> = ({
  order,
  variant = "default",
  isLast = false,
}) => {
  const navigate = useNavigate();
  const orderStatus = order.status;
  const isCompact = variant === "compact";
  const isDraft = orderStatus === GqlOrderStatus.Draft;
  const { user } = useAuthContext();

  const [updateOrder] = useMutation(UpdateOrderDocument, {
    refetchQueries: ["getManyOrdersOfUser"],
  });

  const resumeDraftOrder = useResumeDraftOrder();

  const openDeleteModal = () => {
    openConfirmModal({
      title: "Supprimer la commande",
      centered: true,
      children: (
        <Text size="sm">Voulez-vous vraiment supprimer cette commande ? </Text>
      ),
      labels: {
        confirm: "Supprimer",
        cancel: "Annuler",
      },
      confirmProps: { color: "red" },
      onCancel: () => null,
      onConfirm: () => {
        updateOrder({
          variables: {
            where: {
              id: order.id,
            },
            data: {
              isArchived: {
                set: true,
              },
            },
          },
        }).then(() => {
          notifications.show({
            color: "green",
            title: "Commande supprimée",
            message: "",
          });
        });
      },
    });
  };

  const rowContent = (
    <Flex
      key={order.id}
      align="center"
      justify="space-between"
      h="5rem"
      my={isCompact ? 0 : "sm"}
      mx={isCompact ? 0 : "0.5rem"}
      px={isCompact ? 0 : "lg"}
      bdrs={isCompact ? 0 : "md"}
      bd={
        isCompact
          ? undefined
          : `2px solid ${isDraft ? "pink.3" : "greyScale.8"}`
      }
      bg={!isCompact && isDraft ? "pink.0" : "white"}
      style={{
        borderBottom:
          isCompact && !isLast
            ? "1px solid var(--mantine-color-greyScale-8)"
            : undefined,
        cursor: isDraft ? "auto" : "pointer",
      }}
      onClick={isDraft ? undefined : () => navigate(`/order/${order.id}`)}
    >
      <Box>
        <Title order={5} c="greyScale.0">
          {orderStatus === GqlOrderStatus.Draft
            ? `${
                user?.managedUserGroup
                  ? order.user.id === user?.id
                    ? `${order.user.firstName} ${order.user.lastName} : `
                    : `Dr. ${order.user.lastName} : `
                  : ""
              }Commande pour ${getPatientLabelFirstLine(order)} non terminée`
            : `${getPatientLabelFirstLine(order)}${
                user?.managedUserGroup ? ` (Dr. ${order.user.lastName})` : ""
              }${(order?.redo ?? []).length > 0 ? " - refabrication" : ""}`}
        </Title>

        <Text size="sm" truncate w={250}>
          {`${getProductsOrderedCountLabel(order)}`}
        </Text>
        {orderStatus !== GqlOrderStatus.Draft && order.orderReference && (
          <Text size="xs" c="dimmed">
            {order.orderReference}
          </Text>
        )}
      </Box>
      {orderStatus === GqlOrderStatus.Draft ? (
        <Group gap="md">
          <Text
            size="sm"
            fw={700}
            style={{ cursor: "pointer" }}
            onClick={openDeleteModal}
          >
            Supprimer
          </Text>
          <Button
            onClick={() =>
              resumeDraftOrder({
                id: order.id,
                createdAt: order.createdAt,
                productTypes: (order?.products ?? []).map(
                  item => item.productType
                ),
              })
            }
          >
            Continuer
          </Button>
        </Group>
      ) : (
        <OrderStatusStepper
          order={order as Order}
          orderStatus={getOrderStatusStep(
            order.status as string as SharedOrderStatus
          )}
        />
      )}
    </Flex>
  );

  return rowContent;
};
